import React, {useEffect, useState} from "react";
import {PencilSquareIcon, PlusIcon, TrashIcon,} from "@heroicons/react/24/outline";
import {Link} from "react-router-dom";
import {IoArrowBackSharp} from "react-icons/io5";

import axiosInstance from "../../utils/axiosInstance";
import Loader from "../../components/Loader";
import Notification from "../../components/Notification";

const AdditionalPrice = () => {
    const [additionalPrices, setAdditionalPrices] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [error, setError] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [priceToDelete, setPriceToDelete] = useState(null);
    const [showNotification, setShowNotification] = useState(false);

    useEffect(() => {
        fetchAdditionalPrices();
    }, []);

    const fetchAdditionalPrices = async () => {
        try {
            const response = await axiosInstance.get("/additional-prices");
            setAdditionalPrices(response.data);
        } catch (err) {
            setError("Failed to load additional prices.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (price) => {
        setPriceToDelete(price);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setButtonLoading(true);
            await axiosInstance.delete(
                `/additional-prices/${priceToDelete.id}`
            );
            setAdditionalPrices(
                additionalPrices.filter((p) => p.id !== priceToDelete.id)
            );
            setShowDeleteModal(false);
            setPriceToDelete(null);
            setShowNotification(true);
        } catch (err) {
            setError("Failed to delete additional price.");
        } finally {
            setButtonLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setPriceToDelete(null);
    };

    const filteredPrices = additionalPrices.filter(
        (item) =>
            item.column.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
                <Loader/>
            </div>
        );
    }

    if (error) {
        return <p className="text-center text-sm font-medium text-rose-600 dark:text-rose-400">{error}</p>;
    }

    return (
        <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center">
                    <button
                        onClick={() => window.history.back()}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition hover:border-red-400/60 hover:text-red-500 dark:border-[#3a3a3d] dark:text-slate-400"
                    >
                        <IoArrowBackSharp size={24}/>
                    </button>
                <span className="h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-red-500 to-orange-400" />
                    Additional Price List
                </h3>

                <Link
                    to="/admin/add-additional-price"
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-600 to-orange-500 px-6 text-sm font-bold text-white shadow-lg shadow-red-500/25 transition hover:from-red-500 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <PlusIcon className="h-5 w-5 mr-2"/>
                    Add Additional Price
                </Link>
            </div>

            {/* Search */}
            <div className="mb-4">
                <input
                    type="text"
                    className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                    placeholder="Search by column or value..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Table */}
            <table className="min-w-full border-separate border-spacing-0 overflow-hidden rounded-[16px] border border-gray-200 bg-white text-sm dark:border-[#2c2c2e] dark:bg-[#1c1c1e]">
                <thead>
                <tr className="bg-gray-100 dark:bg-[#161617]">
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Column</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Value</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Price</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Rule</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Actions</th>
                </tr>
                </thead>
                <tbody>
                {filteredPrices.map((item) => (
                    <tr key={item.id} className="transition hover:bg-gray-50 dark:hover:bg-[#232325]">
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{item.column}</td>
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{item.value}</td>
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{item.credits}</td>
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{item.rule}</td>
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300 flex items-center gap-1">
                            <Link
                                to={`/admin/edit-additional-price/${item.id}`}
                                className="text-yellow-500 p-1 rounded mr-2 hover:bg-yellow-600 hover:text-white"
                            >
                                <PencilSquareIcon className="h-5 w-5"/>
                            </Link>
                            <button
                                className="text-red-500 p-1 rounded hover:bg-red-600 hover:text-white"
                                onClick={() => handleDeleteClick(item)}
                            >
                                <TrashIcon className="h-5 w-5"/>
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-md p-6 rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40">
                        <h2 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">Are you sure?</h2>
                        <p>
                            Delete additional price for{" "}
                            <strong>{priceToDelete.value}</strong>?
                        </p>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-5 text-sm font-semibold text-gray-700 transition hover:border-red-400/60 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-200"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-rose-500 px-6 text-sm font-bold text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-60"
                                onClick={handleDeleteConfirm}
                                disabled={buttonLoading}
                            >
                                {buttonLoading ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification */}
            {showNotification && (
                <Notification
                    setShowNotification={setShowNotification}
                    message="Additional price deleted successfully!"
                />
            )}
        </div>
    );
};

export default AdditionalPrice;